import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Video, Star, CheckCircle2, Award, Shield, ChevronRight, PlayCircle, MessageSquare, Heart, Share2, BookOpen, ArrowRight, Users } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Rating } from '../components/ui/Rating';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface ProductDetailPageProps {
  onNavigate: (page: string) => void;
}
export function ProductDetailPage({
  onNavigate
}: ProductDetailPageProps) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const {
    addItem
  } = useCart();
  const handleBuyProduct = () => {
    addItem({
      id: `course-${Date.now()}`,
      name: 'Основы ведической астрологии: с нуля до первых прогнозов',
      description: 'Научитесь читать свою натальную карту за 4 недели. Практические задания, обратная связь от кураторов и сертификат по окончании.',
      price: 15000,
      oldPrice: 18000,
      type: 'course',
      astrologer: {
        name: 'Михаил Волков',
        initials: 'МВ',
        rating: 5.0,
        reviews: 89
      },
      format: 'Онлайн-курс',
      duration: '4 недели',
      badges: [{
        label: 'Онлайн-курс',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href);
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 2000);
  };
  const discountPercent = Math.round((1 - 15000 / 18000) * 100);
  const curriculum = [{
    id: '1',
    title: 'Модуль 1: Основы и стихии',
    content: 'Знакомство с астрологией. 4 стихии, кресты качеств. Как стихии проявляются в характере человека. Практика: определение ведущей стихии.'
  }, {
    id: '2',
    title: 'Модуль 2: Знаки зодиака',
    content: 'Подробный разбор каждого из 12 знаков. Светлая и теневая сторона. Мотивация знаков. Практика: анализ Солнца и Луны в знаках.'
  }, {
    id: '3',
    title: 'Модуль 3: Планеты',
    content: 'Личные, социальные и высшие планеты. Функции планет в психике. Ретроградность. Практика: разбор личных планет в карте.'
  }, {
    id: '4',
    title: 'Модуль 4: Дома гороскопа',
    content: 'Сферы жизни. Угловые, последующие и падающие дома. Управители домов. Практика: синтез планета + знак + дом.'
  }];
  const relatedServices = [{
    id: 1,
    title: 'Консультация: Предназначение и карьера',
    astrologer: 'Анна Коваль',
    initials: 'АК',
    avatarColor: 'bg-amber-100 text-amber-700',
    price: '5 500 ₽',
    rating: 4.9,
    reviews: 67,
    format: 'Видеозвонок',
    duration: '90 мин'
  }, {
    id: 2,
    title: 'Марафон: Проработка Венеры',
    astrologer: 'Михаил Волков',
    initials: 'МВ',
    avatarColor: 'bg-emerald-100 text-emerald-700',
    price: '5 000 ₽',
    rating: 4.8,
    reviews: 312,
    format: 'Telegram',
    duration: '14 дней'
  }, {
    id: 3,
    title: 'Письменный разбор: Синастрия',
    astrologer: 'Дмитрий Орлов',
    initials: 'ДО',
    avatarColor: 'bg-rose-100 text-rose-700',
    price: '3 000 ₽',
    rating: 4.9,
    reviews: 128,
    format: 'PDF-файл',
    duration: '3-4 дня'
  }, {
    id: 4,
    title: 'Вебинар: Лунные циклы и планирование',
    astrologer: 'София Романова',
    initials: 'СР',
    avatarColor: 'bg-blue-100 text-blue-700',
    price: '1 200 ₽',
    rating: 4.6,
    reviews: 34,
    format: 'Запись + PDF',
    duration: '1.5 часа'
  }];
  return <div className="w-full bg-slate-50 min-h-screen pb-32 lg:pb-24" data-id="element-21933">
      {/* Hero Section */}
      <div className="bg-slate-900 text-white pt-8 pb-16" data-id="element-21934">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21935">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Маркетплейс',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Курсы и марафоны',
          onClick: () => onNavigate('marketplace')
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-21936" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-21937">
            <div data-id="element-21938">
              <div className="flex items-center gap-3 mb-6" data-id="element-21939">
                <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30" data-id="element-21940">
                  Онлайн-курс
                </Badge>
                <Badge variant="success" className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-id="element-21941">
                  −{discountPercent}%
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-21942">
                Основы ведической астрологии: с нуля до первых прогнозов
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-21943">
                Научитесь читать свою натальную карту за 4 недели. Практические
                задания, обратная связь от кураторов и сертификат по окончании.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-300 mb-6" data-id="element-21944">
                <div className="flex items-center" data-id="element-21945">
                  <Rating value={5.0} showCount={false} size="sm" data-id="element-21946" />
                  <span className="ml-2 font-medium text-white" data-id="element-21947">5.0</span>
                  <span className="ml-1 text-slate-400" data-id="element-21948">(89 отзывов)</span>
                </div>
                <div className="flex items-center" data-id="element-21949">
                  <Clock className="w-4 h-4 mr-2 text-slate-400" data-id="element-21950" />4 недели
                </div>
                <div className="flex items-center" data-id="element-21951">
                  <Video className="w-4 h-4 mr-2 text-slate-400" data-id="element-21952" />
                  24 видеоурока
                </div>
                <div className="flex items-center" data-id="element-21953">
                  <Users className="w-4 h-4 mr-2 text-slate-400" data-id="element-21954" />
                  340+ учеников
                </div>
              </div>

              {/* Share & Favorite */}
              <div className="flex items-center gap-3" data-id="element-21955">
                <button onClick={() => setIsFavorite(!isFavorite)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${isFavorite ? 'bg-rose-500/20 border-rose-500/40 text-rose-300' : 'bg-white/5 border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`} aria-label={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'} data-id="element-21956">
                  <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-400' : ''}`} data-id="element-21957" />
                  {isFavorite ? 'В избранном' : 'В избранное'}
                </button>
                <button onClick={handleShare} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-700 bg-white/5 text-slate-400 hover:text-white hover:border-slate-500 text-sm font-medium transition-all" aria-label="Поделиться" data-id="element-21958">
                  <Share2 className="w-4 h-4" data-id="element-21959" />
                  Поделиться
                </button>
                {showShareToast && <span className="text-xs text-emerald-400 animate-pulse" data-id="element-21960">
                    Ссылка скопирована!
                  </span>}
              </div>
            </div>

            <div className="relative" data-id="element-21961">
              <div className="aspect-video bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 shadow-2xl relative group cursor-pointer flex items-center justify-center" role="button" aria-label="Смотреть превью курса" tabIndex={0} data-id="element-21962">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/40 to-slate-800/40 mix-blend-overlay" data-id="element-21963"></div>
                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-primary transition-colors z-10" data-id="element-21964">
                  <PlayCircle className="w-8 h-8 text-white ml-1" data-id="element-21965" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-21966">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-21967">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-12" data-id="element-21968">
            {/* About */}
            <section data-id="element-21969">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-21970">
                О курсе
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600" data-id="element-21971">
                <p data-id="element-21972">
                  Этот курс создан специально для тех, кто хочет изучать
                  астрологию "для себя", но готов погрузиться глубоко. Мы не
                  будем заучивать сухие трактовки из интернета. Вы научитесь
                  понимать логику астрологии, синтезировать показатели и видеть
                  за символами живого человека.
                </p>
                <p data-id="element-21973">
                  Уже после первой недели обучения вы сможете построить свою
                  карту и понять свои базовые потребности, таланты и точки
                  напряжения.
                </p>
              </div>
            </section>

            {/* What you'll learn */}
            <section data-id="element-21974">
              <Card padding="lg" className="bg-indigo-50/50 border-indigo-100" data-id="element-21975">
                <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-21976">
                  Чему вы научитесь:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-21977">
                  {['Строить натальную карту в профессиональной программе', 'Определять сильные и слабые стороны личности', 'Видеть финансовый потенциал и подходящие профессии', 'Понимать кармические задачи (Лунные узлы)', 'Анализировать сферу отношений и брака', 'Подбирать компенсаторные действия для сложных аспектов'].map((item, i) => <div key={i} className="flex items-start" data-id="element-21978">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-21979" />
                      <span className="text-sm text-slate-700 leading-relaxed" data-id="element-21980">
                        {item}
                      </span>
                    </div>)}
                </div>
              </Card>
            </section>

            {/* Curriculum */}
            <section data-id="element-21981">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-21982">
                Программа обучения
              </h2>
              <Accordion items={curriculum} data-id="element-21983" />
            </section>

            {/* Reviews */}
            <section data-id="element-21984">
              <div className="flex items-center justify-between mb-8" data-id="element-21985">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-21986">
                  Отзывы учеников
                </h2>
                <div className="flex items-center" data-id="element-21987">
                  <Rating value={5.0} showCount={false} data-id="element-21988" />
                  <span className="ml-2 font-bold text-slate-900" data-id="element-21989">5.0</span>
                </div>
              </div>

              <div className="space-y-6" data-id="element-21990">
                {[{
                name: 'Ольга С.',
                initial: 'О',
                batch: 'Выпускница 3 потока',
                text: 'Потрясающий курс! Михаил объясняет сложные вещи очень простым языком. Я пыталась изучать астрологию сама по книгам, но в голове была каша. Здесь же всё разложили по полочкам. Особенно ценно, что мы практиковались на своих картах.'
              }, {
                name: 'Мария В.',
                initial: 'М',
                batch: 'Выпускница 4 потока',
                text: 'Лучшая инвестиция в себя за последний год. Я наконец-то поняла, почему у меня постоянно возникали одни и те же проблемы в отношениях. Кураторы отвечали на вопросы очень подробно. Рекомендую всем!'
              }, {
                name: 'Екатерина Д.',
                initial: 'Е',
                batch: 'Выпускница 5 потока',
                text: 'Очень структурированная подача материала. Каждый модуль логично вытекает из предыдущего. После курса я уже делаю разборы для подруг и они в восторге. Спасибо Михаилу за его подход!'
              }].map((review, i) => <Card key={i} padding="lg" data-id="element-21991">
                    <div className="flex justify-between items-start mb-4" data-id="element-21992">
                      <div className="flex items-center gap-3" data-id="element-21993">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500" data-id="element-21994">
                          {review.initial}
                        </div>
                        <div data-id="element-21995">
                          <div className="font-bold text-slate-900" data-id="element-21996">
                            {review.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-21997">
                            {review.batch}
                          </div>
                        </div>
                      </div>
                      <Rating value={5} showCount={false} size="sm" data-id="element-21998" />
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-21999">
                      {review.text}
                    </p>
                  </Card>)}
              </div>
              <Button variant="outline" fullWidth className="mt-6" data-id="element-22000">
                Показать все отзывы (89)
              </Button>
            </section>

            {/* Related Services */}
            <section data-id="element-22001">
              <div className="flex items-center justify-between mb-6" data-id="element-22002">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-22003">
                  Похожие товары
                </h2>
                <button onClick={() => onNavigate('marketplace')} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center" data-id="element-22004">
                  Все услуги <ArrowRight className="w-4 h-4 ml-1" data-id="element-22005" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-22006">
                {relatedServices.map(service => <div key={service.id} className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer group hover:shadow-md transition-shadow" onClick={() => onNavigate('product-detail')} data-id="element-22007">
                    <div className="flex items-center gap-2.5 mb-3" data-id="element-22008">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${service.avatarColor}`} data-id="element-22009">
                        {service.initials}
                      </div>
                      <div className="min-w-0" data-id="element-22010">
                        <div className="text-xs font-medium text-slate-500 truncate" data-id="element-22011">
                          {service.astrologer}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-22012">
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-22013" />
                          <span className="text-xs font-medium text-slate-700" data-id="element-22014">
                            {service.rating}
                          </span>
                          <span className="text-[10px] text-slate-400" data-id="element-22015">
                            ({service.reviews})
                          </span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors leading-snug" data-id="element-22016">
                      {service.title}
                    </h3>
                    <div className="flex items-center gap-3 mb-3 text-xs text-slate-500" data-id="element-22017">
                      <span className="flex items-center" data-id="element-22018">
                        <BookOpen className="w-3 h-3 mr-1 text-slate-400" data-id="element-22019" />{' '}
                        {service.format}
                      </span>
                      <span className="flex items-center" data-id="element-22020">
                        <Clock className="w-3 h-3 mr-1 text-slate-400" data-id="element-22021" />{' '}
                        {service.duration}
                      </span>
                    </div>
                    <div className="text-base font-bold text-slate-900" data-id="element-22022">
                      {service.price}
                    </div>
                  </div>)}
              </div>
            </section>
          </div>

          {/* Right Column: Sticky Buy Box & Astrologer Info */}
          <div className="lg:col-span-4" data-id="element-22023">
            <div className="sticky top-24 space-y-6" data-id="element-22024">
              {/* Buy Box */}
              <Card padding="lg" className="border-primary/20 shadow-xl" data-id="element-22025">
                <div className="mb-6" data-id="element-22026">
                  <div className="flex items-center gap-3 mb-1" data-id="element-22027">
                    <span className="text-sm text-slate-500 line-through" data-id="element-22028">
                      18 000 ₽
                    </span>
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-22029">
                      −{discountPercent}%
                    </span>
                  </div>
                  <div className="text-4xl font-bold text-slate-900" data-id="element-22030">
                    15 000 ₽
                  </div>
                </div>

                <div className="space-y-4 mb-8" data-id="element-22031">
                  <div className="flex items-center text-sm text-slate-600" data-id="element-22032">
                    <Video className="w-5 h-5 mr-3 text-slate-400" data-id="element-22033" />
                    Доступ к 24 видеоурокам навсегда
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-22034">
                    <MessageSquare className="w-5 h-5 mr-3 text-slate-400" data-id="element-22035" />
                    Чат с кураторами и автором
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-22036">
                    <Award className="w-5 h-5 mr-3 text-slate-400" data-id="element-22037" />
                    Сертификат по окончании
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-22038">
                    <Users className="w-5 h-5 mr-3 text-slate-400" data-id="element-22039" />
                    340+ учеников уже прошли
                  </div>
                </div>

                <Button size="lg" fullWidth onClick={handleBuyProduct} className="mb-4 shadow-md shadow-primary/20" data-id="element-22040">
                  Записаться на курс
                </Button>

                <div className="flex items-center justify-center text-xs text-slate-500" data-id="element-22041">
                  <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-22042" />
                  Безопасная оплата. Гарантия возврата 14 дней.
                </div>
              </Card>

              {/* Astrologer Card */}
              <Card padding="md" hoverable onClick={() => onNavigate('astrologer-profile')} data-id="element-22043">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-22044">
                  Автор курса
                </h3>
                <div className="flex items-center gap-4 mb-4" data-id="element-22045">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-xl font-bold text-emerald-700 flex-shrink-0" data-id="element-22046">
                    МВ
                  </div>
                  <div data-id="element-22047">
                    <div className="font-bold text-slate-900" data-id="element-22048">
                      Михаил Волков
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-22049">
                      Ведическая астрология
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-4 line-clamp-3" data-id="element-22050">
                  Практикующий астролог с опытом более 10 лет. Основатель школы
                  ведической астрологии. Более 5000 проведенных консультаций.
                </p>
                <div className="flex items-center text-sm font-medium text-primary" data-id="element-22051">
                  Перейти в профиль <ChevronRight className="w-4 h-4 ml-1" data-id="element-22052" />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-22053">
        <div data-id="element-22054">
          <div className="flex items-center gap-2" data-id="element-22055">
            <span className="text-xs text-slate-400 line-through" data-id="element-22056">
              18 000 ₽
            </span>
            <span className="text-[10px] font-bold text-emerald-600" data-id="element-22057">
              −{discountPercent}%
            </span>
          </div>
          <div className="text-xl font-bold text-slate-900" data-id="element-22058">15 000 ₽</div>
        </div>
        <Button size="md" onClick={handleBuyProduct} className="shadow-md shadow-primary/20" data-id="element-22059">
          Записаться
        </Button>
      </div>
    </div>;
}